import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from '../i18n/LanguageContext';

/* ── Hostname is stable — computed once at module level ─────────────────── */
const _hostname = window.location.hostname;
const _isTenantSubdomain = (
  _hostname.endsWith('.speakiq.uk') &&
  !['admin', 'tutors', 'www'].some(s => _hostname === s + '.speakiq.uk') &&
  _hostname !== 'speakiq.uk'
);
const _schoolName = (() => {
  const sub = _hostname.split('.')[0];
  return (!['www','app','admin','tutors'].includes(sub) && _hostname.includes('.speakiq.uk'))
    ? sub.charAt(0).toUpperCase() + sub.slice(1).replace(/-/g, ' ')
    : 'SpeakIQ';
})();

/* ── PORTAL must be computed from pathname — evaluated inside components ── */
function detectPortal(pathname) {
  const isAdminPath = pathname === '/admin/login' || pathname.startsWith('/admin/login');
  if (_hostname === 'admin.speakiq.uk')          return 'superadmin';
  if (_hostname === 'tutors.speakiq.uk')         return 'tutor';
  if (_isTenantSubdomain && isAdminPath)         return 'partner';
  if (_isTenantSubdomain)                        return 'student';
  return 'student';
}

/* ── Themes ─────────────────────────────────────────────────────────────── */
const THEMES = {
  student:    { panelBg:'linear-gradient(145deg,#00B4D8 0%,#0087A8 100%)', accent:'#00B4D8', btnBg:'#00B4D8', btnHover:'#0087A8', expectedRole:'student' },
  partner:    { panelBg:'linear-gradient(145deg,#0D1B2A 0%,#0a1520 100%)', accent:'#C9A84C', btnBg:'#C9A84C', btnHover:'#a8873a', expectedRole:'tenant_admin' },
  superadmin: { panelBg:'linear-gradient(145deg,#060D14 0%,#0d1a26 100%)', accent:'#DC2626', btnBg:'#DC2626', btnHover:'#b91c1c', expectedRole:'superadmin' },
  tutor:      { panelBg:'linear-gradient(145deg,#064E3B 0%,#065F46 100%)', accent:'#10B981', btnBg:'#10B981', btnHover:'#059669', expectedRole:'tutor' },
};

/* Legacy module-level aliases — updated per render via component props ──── */
let PORTAL = 'student';
let T      = THEMES['student'];
let PORTAL_LABEL = 'Student Portal';
let isTenantSubdomain = _isTenantSubdomain;
let schoolName = _schoolName;

/* ══════════════════════════════════════════════════════════════════════════ */
export default function Login() {
  const [form, setForm]       = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 680);
  // Forgot-password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t, setLanguage } = useTranslation();

  // Re-compute portal on every render so React Router navigation is reflected
  PORTAL           = detectPortal(location.pathname);
  T                = THEMES[PORTAL] || THEMES['student'];
  PORTAL_LABEL     = PORTAL === 'superadmin' ? 'Super Admin' :
                     PORTAL === 'partner'    ? `${_schoolName} Admin` :
                     PORTAL === 'tutor'      ? 'Tutor Portal' : 'Student Portal';
  isTenantSubdomain = _isTenantSubdomain;
  schoolName        = _schoolName;

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setForgotError('Please enter your email address.'); return; }
    setForgotLoading(true); setForgotError('');
    try {
      await api.post('/api/auth/forgot-password', {
        email: forgotEmail.trim().toLowerCase(),
        base_url: window.location.origin,
      });
      setForgotSent(true);
    } catch {
      setForgotError('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 680);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error('Please fill in all fields'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { ...form, expected_role: T.expectedRole });
      setAuth(res.data.token, res.data.user);
      if (res.data.language) setLanguage(res.data.language);
      const role = res.data.user?.role;
      if (role === 'superadmin')    navigate('/superadmin');
      else if (role === 'tenant_admin') navigate('/admin');
      else if (role === 'tutor')    navigate('/tutor');
      else {
        // Students always land on their dashboard — it already has the "Start
        // Assessment" and "Book a Mock Test" actions, so we don't force them in.
        const student = res.data.user;
        if (student.is_mock_candidate) {
          navigate('/my-mock-tests');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: '100%', padding: isMobile ? '14px 16px' : '12px 16px',
    border: `2px solid ${focused === field ? T.accent : '#E5E7EB'}`,
    borderRadius: 10, fontSize: isMobile ? 16 : 15, outline: 'none',
    transition: 'border-color 0.2s', background: '#fff', color: '#1F2937',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
  });

  /* ── Forgot-password panel (shared mobile + desktop) ── */
  const forgotPanel = (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {forgotSent ? (
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📬</div>
          <h3 style={{ fontSize:18, fontWeight:800, color:'#111827', margin:'0 0 8px' }}>Check your inbox</h3>
          <p style={{ color:'#6B7280', fontSize:14, lineHeight:1.6, margin:'0 0 24px' }}>
            If that email is registered, you'll receive a password reset link shortly. Check your spam folder too.
          </p>
          <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
            style={{ background:T.btnBg, color:'#fff', border:'none', borderRadius:10, padding:'12px 24px', fontSize:15, fontWeight:700, cursor:'pointer', width:'100%' }}>
            Back to Login
          </button>
        </div>
      ) : (
        <form onSubmit={handleForgot} style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div>
            <h3 style={{ fontSize:20, fontWeight:800, color:'#111827', margin:'0 0 6px' }}>Forgot your password?</h3>
            <p style={{ color:'#6B7280', fontSize:14, margin:'0 0 20px', lineHeight:1.5 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Email address</label>
            <input type="email" value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ width:'100%', padding:'12px 16px', border:`2px solid ${T.accent}`, borderRadius:10, fontSize:15, outline:'none', boxSizing:'border-box' }}
              autoFocus autoCapitalize="none" autoCorrect="off"
            />
          </div>
          {forgotError && (
            <div style={{ background:'#FEE2E2', color:'#EF4444', padding:'10px 14px', borderRadius:8, fontSize:13 }}>{forgotError}</div>
          )}
          <button type="submit" disabled={forgotLoading}
            style={{ padding:'13px', background: forgotLoading ? '#9CA3AF' : T.btnBg, color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:700, cursor: forgotLoading ? 'default' : 'pointer' }}>
            {forgotLoading ? 'Sending…' : 'Send Reset Link'}
          </button>
          <button type="button" onClick={() => { setShowForgot(false); setForgotError(''); }}
            style={{ background:'none', border:'none', color:'#6B7280', fontSize:14, cursor:'pointer', padding:'4px 0', textDecoration:'underline' }}>
            ← Back to login
          </button>
        </form>
      )}
    </div>
  );

  /* ── Mobile layout ─────────────────────────────────── */
  if (isMobile) {
    return (
      <div style={{ minHeight:'100vh', background:'#F9FAFB', display:'flex', flexDirection:'column' }}>
        <Toaster position="top-center" />

        {/* Compact coloured header */}
        <div style={{ background: T.panelBg, padding:'32px 24px 28px', textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:8 }}>
            {PORTAL==='superadmin' ? '🛡️' : PORTAL==='partner' ? '🏫' : PORTAL==='tutor' ? '🎓' : '🎓'}
          </div>
          <div style={{ fontWeight:900, fontSize:22, color:'#fff', letterSpacing:'-0.3px' }}>SpeakIQ</div>
          <div style={{ marginTop:6, display:'inline-flex', alignItems:'center', gap:6,
            background:'rgba(255,255,255,0.15)', borderRadius:20, padding:'4px 14px' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:T.accent==='#00B4D8'?'#fff':T.accent, display:'inline-block' }} />
            <span style={{ fontSize:11, fontWeight:700, color:'#fff', letterSpacing:1, textTransform:'uppercase' }}>
              {PORTAL_LABEL}
            </span>
          </div>
          {PORTAL === 'student' && (
            <p style={{ color:'rgba(255,255,255,0.8)', fontSize:13, margin:'10px 0 0', lineHeight:1.5 }}>
              Your English Learning Centre
            </p>
          )}
          {PORTAL === 'partner' && (
            <p style={{ color:'rgba(255,255,255,0.7)', fontSize:13, margin:'10px 0 0' }}>
              {schoolName} · Partner Admin Portal
            </p>
          )}
          {PORTAL === 'tutor' && (
            <p style={{ color:'rgba(255,255,255,0.8)', fontSize:13, margin:'10px 0 0' }}>
              Teach English with AI Support
            </p>
          )}
        </div>

        {/* Form card */}
        <div style={{ flex:1, padding:'28px 20px 40px', maxWidth:480, width:'100%', margin:'0 auto', boxSizing:'border-box' }}>

          {showForgot ? forgotPanel : (
            <>
              <h2 style={{ margin:'0 0 4px', fontSize:24, fontWeight:800, color:'#111827', textAlign:'center' }}>
                {PORTAL === 'superadmin' ? 'Command Centre Access' : 'Welcome back'}
              </h2>
              <p style={{ margin:'0 0 28px', color:'#6B7280', fontSize:14, textAlign:'center' }}>
                {PORTAL === 'superadmin' ? 'Authorised credentials only' :
                 PORTAL === 'partner'    ? `Sign in to manage ${schoolName}` :
                 PORTAL === 'tutor'      ? 'Sign in to your tutor account' :
                 'Access your learning portal'}
              </p>

              <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <div>
                  <label style={{ fontSize:14, fontWeight:600, color:'#374151', display:'block', marginBottom:8 }}>
                    {t('login_email')}
                  </label>
                  <input type="email" value={form.email}
                    onChange={e => setForm({...form, email:e.target.value})}
                    placeholder="you@example.com"
                    style={inputStyle('email')}
                    onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
                    autoCapitalize="none" autoCorrect="off"
                  />
                </div>
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <label style={{ fontSize:14, fontWeight:600, color:'#374151' }}>
                      {t('login_password')}
                    </label>
                    {PORTAL !== 'superadmin' && (
                      <button type="button" onClick={() => { setShowForgot(true); setForgotEmail(form.email); }}
                        style={{ background:'none', border:'none', color:T.accent, fontSize:13, fontWeight:600, cursor:'pointer', padding:0 }}>
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <input type="password" value={form.password}
                    onChange={e => setForm({...form, password:e.target.value})}
                    placeholder="••••••••"
                    style={inputStyle('password')}
                    onFocus={() => setFocused('password')} onBlur={() => setFocused('')}
                  />
                </div>

                <button type="submit" disabled={loading}
                  style={{
                    padding:'16px', marginTop:4,
                    background: loading ? '#9CA3AF' : T.btnBg,
                    color:'#fff', border:'none', borderRadius:12,
                    fontSize:17, fontWeight:800,
                    cursor: loading ? 'default' : 'pointer',
                    transition:'background 0.2s',
                    WebkitTapHighlightColor:'transparent',
                  }}>
                  {loading ? t('login_loading') : t('btn_login') + ' →'}
                </button>
              </form>

              {/* Cross-portal links */}
              <div style={{ marginTop:28, display:'flex', flexDirection:'column', gap:12, textAlign:'center' }}>
                {PORTAL === 'student' && (
                  <>
                    <p style={{ color:'#6B7280', fontSize:14, margin:0 }}>
                      New student?{' '}
                      <Link to="/register" style={{ color:T.accent, fontWeight:700, textDecoration:'none' }}>
                        Start Free Assessment
                      </Link>
                    </p>
                    <p style={{ color:'#6B7280', fontSize:14, margin:0 }}>
                      Are you a tutor?{' '}
                      <a href="https://tutors.speakiq.uk/login" style={{ color:T.accent, fontWeight:600, textDecoration:'none' }}>
                        Tutor login →
                      </a>
                    </p>
                    {isTenantSubdomain && (
                      <p style={{ color:'#9CA3AF', fontSize:13, margin:0 }}>
                        School admin?{' '}
                        <Link to="/admin/login" style={{ color:'#C9A84C', fontWeight:700, textDecoration:'none' }}>
                          Admin login →
                        </Link>
                      </p>
                    )}
                  </>
                )}
                {PORTAL === 'tutor' && (
                  <>
                    <p style={{ color:'#9CA3AF', fontSize:13, margin:0 }}>Not registered yet?</p>
                    <a href="/tutor-signup" style={{
                      display:'block', padding:'14px', background:T.btnBg,
                      color:'#fff', borderRadius:10, fontWeight:700, fontSize:15, textDecoration:'none'
                    }}>🚀 Apply to Teach →</a>
                  </>
                )}
                {PORTAL === 'partner' && (
                  <p style={{ color:'#9CA3AF', fontSize:13, margin:0 }}>
                    Need help?{' '}
                    <a href="mailto:hello@speakiq.uk" style={{ color:T.accent, fontWeight:600, textDecoration:'none' }}>
                      hello@speakiq.uk
                    </a>
                  </p>
                )}
                {PORTAL === 'superadmin' && (
                  <p style={{ color:'#9CA3AF', fontSize:12, margin:0 }}>
                    🔒 This system is monitored and all access is logged
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── Desktop layout (two-panel) ────────────────────── */
  return (
    <div style={{
      minHeight:'100vh',
      background:'linear-gradient(135deg,#060D14 0%,#0D1B2A 100%)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
    }}>
      <Toaster position="top-right" />
      <div style={{
        display:'flex', maxWidth:920, width:'100%', minHeight:580,
        borderRadius:20, overflow:'hidden',
        boxShadow:'0 30px 80px rgba(0,0,0,0.5)',
      }}>
        <LeftPanel />

        {/* Right form panel */}
        <div style={{ flex:1, background:'#fff', padding:'48px 44px', display:'flex', flexDirection:'column', justifyContent:'center' }}>

          {showForgot ? forgotPanel : (
            <>
              {/* Portal badge */}
              <div style={{ display:'inline-flex', alignItems:'center', gap:6,
                background:T.accent+'18', border:`1px solid ${T.accent}40`,
                borderRadius:20, padding:'4px 12px', marginBottom:24, width:'fit-content' }}>
                <span style={{ width:7, height:7, borderRadius:'50%', background:T.accent, display:'inline-block' }} />
                <span style={{ fontSize:11, fontWeight:700, color:T.accent, letterSpacing:1, textTransform:'uppercase' }}>
                  {PORTAL_LABEL}
                </span>
              </div>

              <h2 style={{ margin:'0 0 6px', fontSize:26, fontWeight:800, color:'#111827' }}>
                {PORTAL === 'superadmin' ? 'Command Centre Access' : 'Welcome back'}
              </h2>
              <p style={{ margin:'0 0 32px', color:'#6B7280', fontSize:14 }}>
                {PORTAL === 'superadmin' ? 'Enter your authorised credentials to continue' :
                 PORTAL === 'partner'    ? `Sign in to manage ${schoolName} partner account` :
                 PORTAL === 'tutor'      ? 'Sign in to your tutor account' :
                 'Access your learning portal'}
              </p>

              <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>
                    {t('login_email')}
                  </label>
                  <input type="email" value={form.email}
                    onChange={e => setForm({...form, email:e.target.value})}
                    placeholder="you@example.com"
                    style={inputStyle('email')}
                    onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
                  />
                </div>
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>
                      {t('login_password')}
                    </label>
                    {PORTAL !== 'superadmin' && (
                      <button type="button" onClick={() => { setShowForgot(true); setForgotEmail(form.email); }}
                        style={{ background:'none', border:'none', color:T.accent, fontSize:13, fontWeight:600, cursor:'pointer', padding:0 }}>
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <input type="password" value={form.password}
                    onChange={e => setForm({...form, password:e.target.value})}
                    placeholder="••••••••"
                    style={inputStyle('password')}
                    onFocus={() => setFocused('password')} onBlur={() => setFocused('')}
                  />
                </div>

                <button type="submit" disabled={loading}
                  style={{
                    padding:'14px', marginTop:8,
                    background: loading ? '#9CA3AF' : T.btnBg,
                    color:'#fff', border:'none', borderRadius:10,
                    fontSize:16, fontWeight:700,
                    cursor: loading ? 'default' : 'pointer',
                    transition:'background 0.2s',
                  }}
                  onMouseEnter={e => { if (!loading) e.target.style.background = T.btnHover; }}
                  onMouseLeave={e => { if (!loading) e.target.style.background = T.btnBg; }}>
                  {loading ? t('login_loading') : t('btn_login') + ' →'}
                </button>
              </form>

              <div style={{ marginTop:28, display:'flex', flexDirection:'column', gap:8 }}>
                {PORTAL === 'student' && (
                  <>
                    <p style={{ textAlign:'center', color:'#6B7280', fontSize:14, margin:0 }}>
                      New student?{' '}
                      <Link to="/register" style={{ color:T.accent, fontWeight:700, textDecoration:'none' }}>
                        Start Free Assessment
                      </Link>
                    </p>
                    <p style={{ textAlign:'center', color:'#6B7280', fontSize:14, margin:0 }}>
                      Are you a tutor?{' '}
                      <a href="https://tutors.speakiq.uk/login" style={{ color:T.accent, fontWeight:600, textDecoration:'none' }}>
                        Tutor login →
                      </a>
                    </p>
                  </>
                )}
                {PORTAL === 'partner' && (
                  <p style={{ textAlign:'center', color:'#9CA3AF', fontSize:13, margin:0 }}>
                    Need help?{' '}
                    <a href="mailto:hello@speakiq.uk" style={{ color:T.accent, fontWeight:600, textDecoration:'none' }}>
                      hello@speakiq.uk
                    </a>
                  </p>
                )}
                {PORTAL === 'tutor' && (
                  <div style={{ textAlign:'center' }}>
                    <p style={{ color:'#9CA3AF', fontSize:14, margin:'0 0 8px' }}>Not registered yet?</p>
                    <a href="/tutor-signup" style={{
                      display:'inline-block', padding:'10px 24px',
                      background:T.btnBg, color:'#fff', borderRadius:8,
                      fontWeight:700, fontSize:14, textDecoration:'none'
                    }}>Apply to Teach →</a>
                  </div>
                )}
                {PORTAL === 'superadmin' && (
                  <p style={{ textAlign:'center', color:'#9CA3AF', fontSize:12, margin:0 }}>
                    🔒 This system is monitored and all access is logged
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
function LeftPanel() {
  if (PORTAL === 'superadmin') return <SuperAdminPanel />;
  if (PORTAL === 'partner')    return <PartnerPanel />;
  if (PORTAL === 'tutor')      return <TutorPanel />;
  return <StudentPanel />;
}

function StudentPanel() {
  return (
    <div style={{ flex:1, background:'linear-gradient(145deg,#00B4D8 0%,#0087A8 100%)', padding:'48px 40px', display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
      <div>
        <div style={{ fontSize:32, marginBottom:16 }}>🎓</div>
        <div style={{ fontWeight:900, fontSize:22, color:'#fff', marginBottom:4 }}>SpeakIQ</div>
        <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13 }}>The Complete English Learning Centre Platform</div>
      </div>
      <div>
        <h2 style={{ color:'#fff', fontSize:28, fontWeight:800, lineHeight:1.3, marginBottom:8 }}>Your English Learning Centre</h2>
        <p style={{ color:'rgba(255,255,255,0.8)', fontSize:14, marginBottom:28 }}>Structured programmes for every level, from A1 to C2</p>
        {['AI practice sessions tailored to your level','Live human tutors when you need them','CEFR-aligned certificates you can share','Track your progress lesson by lesson'].map((item,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, color:'rgba(255,255,255,0.92)', fontSize:14, marginBottom:12 }}>
            <span style={{ width:20, height:20, borderRadius:'50%', background:'rgba(255,255,255,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, flexShrink:0 }}>✓</span>
            {item}
          </div>
        ))}
      </div>
      <div>
        <div style={{ background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.35)', borderRadius:10, padding:'12px 16px', marginBottom:20, color:'#fff', fontSize:13 }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>🎓 Free Full Assessment — No Payment Required</div>
          <div style={{ fontSize:12, opacity:0.85, lineHeight:1.5 }}>Grammar · Vocabulary · Reading · Writing · Listening · Speaking. AI analyses your level and builds your personal CEFR roadmap.</div>
        </div>
        <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>© 2026 SpeakIQ</div>
      </div>
    </div>
  );
}

function PartnerPanel() {
  return (
    <div style={{ flex:1, background:'linear-gradient(145deg,#0D1B2A 0%,#0a1520 100%)', padding:'48px 40px', display:'flex', flexDirection:'column', justifyContent:'space-between', borderRight:'1px solid rgba(201,168,76,0.2)' }}>
      <div>
        <div style={{ fontSize:32, marginBottom:16 }}>🏫</div>
        <div style={{ fontWeight:900, fontSize:22, color:'#C9A84C', marginBottom:4 }}>SpeakIQ</div>
        <div style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>Partner Admin Portal</div>
      </div>
      <div>
        <div style={{ color:'#C9A84C', fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{schoolName}</div>
        <h2 style={{ color:'#fff', fontSize:26, fontWeight:800, lineHeight:1.3, marginBottom:8 }}>Partner Admin Portal</h2>
        <p style={{ color:'rgba(255,255,255,0.6)', fontSize:14, marginBottom:28 }}>Manage your school, students and tutors</p>
        {['Student and tutor management','Revenue and P&L dashboard','AI cost monitoring','Automated marketing tools'].map((item,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, color:'rgba(255,255,255,0.8)', fontSize:14, marginBottom:12 }}>
            <span style={{ width:20, height:20, borderRadius:'50%', background:'rgba(201,168,76,0.2)', border:'1px solid rgba(201,168,76,0.5)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#C9A84C', flexShrink:0 }}>✓</span>
            {item}
          </div>
        ))}
      </div>
      <div>
        <div style={{ background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.3)', borderRadius:10, padding:'10px 16px', marginBottom:20, color:'#C9A84C', fontSize:13, fontWeight:700 }}>
          🏆 {schoolName} · Powered by SpeakIQ
        </div>
        <div style={{ color:'rgba(255,255,255,0.3)', fontSize:12 }}>© 2026 SpeakIQ</div>
      </div>
    </div>
  );
}

function SuperAdminPanel() {
  return (
    <div style={{ flex:1, background:'linear-gradient(145deg,#060D14 0%,#0d1520 100%)', padding:'48px 40px', display:'flex', flexDirection:'column', justifyContent:'space-between', borderRight:'1px solid rgba(220,38,38,0.15)' }}>
      <div>
        <div style={{ width:56, height:56, borderRadius:14, background:'rgba(220,38,38,0.15)', border:'1px solid rgba(220,38,38,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, marginBottom:20 }}>🛡️</div>
        <div style={{ fontWeight:900, fontSize:20, color:'#fff', marginBottom:4 }}>SpeakIQ</div>
        <div style={{ color:'rgba(255,255,255,0.4)', fontSize:12, letterSpacing:2, textTransform:'uppercase' }}>Internal Systems</div>
      </div>
      <div>
        <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(220,38,38,0.15)', border:'1px solid rgba(220,38,38,0.4)', borderRadius:6, padding:'4px 10px', marginBottom:20 }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:'#DC2626', display:'inline-block' }} />
          <span style={{ color:'#DC2626', fontSize:10, fontWeight:800, letterSpacing:1.5, textTransform:'uppercase' }}>Restricted Access</span>
        </div>
        <h2 style={{ color:'#fff', fontSize:26, fontWeight:800, lineHeight:1.3, marginBottom:10 }}>SpeakIQ Command Centre</h2>
        <p style={{ color:'rgba(255,255,255,0.5)', fontSize:14, lineHeight:1.6 }}>Authorised personnel only.<br/>Access to this system is restricted and monitored.</p>
      </div>
      <div>
        <div style={{ background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.2)', borderRadius:8, padding:'12px 14px', color:'rgba(255,255,255,0.4)', fontSize:11, lineHeight:1.6 }}>
          ⚠️ This system is monitored and all access is logged.
        </div>
        <div style={{ color:'rgba(255,255,255,0.2)', fontSize:11, marginTop:20 }}>© 2026 SpeakIQ Technologies</div>
      </div>
    </div>
  );
}

function TutorPanel() {
  return (
    <div style={{ flex:1, background:'linear-gradient(145deg,#064E3B 0%,#065F46 100%)', display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'40px 36px', color:'#fff' }}>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:32 }}>
          <div style={{ width:44, height:44, borderRadius:10, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>🎓</div>
          <div>
            <div style={{ fontWeight:800, fontSize:18 }}>SpeakIQ</div>
            <div style={{ fontSize:12, opacity:0.7, marginTop:2 }}>Tutor Portal</div>
          </div>
        </div>
        <h1 style={{ fontSize:30, fontWeight:900, lineHeight:1.15, margin:'0 0 16px' }}>Teach English<br/>with AI Support</h1>
        <p style={{ fontSize:14, opacity:0.75, lineHeight:1.6, margin:'0 0 28px' }}>Join our network of qualified English tutors. AI handles lesson content — you focus on teaching.</p>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {['Competitive pay per session','AI-generated lesson materials','Flexible schedule — you decide','Student progress tracked automatically','ID card issued on approval'].map((item,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, fontSize:14 }}>
              <div style={{ width:20, height:20, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11 }}>✓</div>
              <span style={{ opacity:0.9 }}>{item}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <a href="/tutor-signup" style={{ display:'block', textAlign:'center', padding:'14px 0', background:'rgba(255,255,255,0.15)', borderRadius:10, color:'#fff', fontWeight:700, fontSize:14, textDecoration:'none', border:'1.5px solid rgba(255,255,255,0.25)', marginBottom:14 }}>
          🚀 Apply to Teach →
        </a>
        <div style={{ fontSize:11, opacity:0.45, textAlign:'center' }}>© 2026 SpeakIQ · Tutors</div>
      </div>
    </div>
  );
}
